import {
  Box,
  Button,
  Flex,
  FormInput,
  Heading,
  IconButton,
  toast,
  Text
} from '@sparrowengg/twigs-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  ResetIcon
} from '@sparrowengg/twigs-react-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AuthenticationHeader from './authentication-header';
import { devPortalServices } from 'commons/services';
import { Spinner } from 'commons/components/spinner';
import { IRootState } from 'commons/types';
import {
  authenticationHeader,
  customFieldDefaultValue,
  defaultValues,
  urlRegex,
  webhookHeaders
} from 'features/webhook/constants';
import { useLocation } from 'react-router-dom';
import TickIcon from 'commons/icons/tick';
import { AppAuthenticationProps } from 'features/webhook/types';

const AppAuthentication = ({
  proceedHandler,
  previousHandler,
  register,
  setValue,
  getValues,
  trigger,
  errors
}: AppAuthenticationProps) => {
  const [loader, setLoader] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [validBtnLoader, setValidBtnLoader] = useState(false);
  const [fields, setFields] = useState([{ ...customFieldDefaultValue }]);
  const isInitialized = useRef(false);
  const location = useLocation();
  const webhookDetails = useSelector((state: IRootState) => state.app);
  const [currentHeader, setCurrentHeader] = useState(
    authenticationHeader.find(
      (header) => header.label === getValues('authentication')
    )?.value ?? webhookHeaders.DEFAULT_HEADER
  );
  const copyTokenToClipboard = () => {
    navigator.clipboard.writeText(getValues('secretKey'));
    toast({
      variant: 'success',
      description: 'Secret Token has been copied to the clipboard.'
    });
  };

  const regenerateToken = async () => {
    setBtnLoader(true);
    try {
      const response = await devPortalServices.regenerateSecretToken();
      setValue('secretKey', response, { shouldDirty: true });
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Error when regenerating the token'
      });
    } finally {
      setBtnLoader(false);
      setLoader(false);
    }
  };

  const validateURL = async () => {
    setValidBtnLoader(true);
    try {
      await devPortalServices.validateURL({
        url: getValues('url'),
        authType: getValues('authentication'),
        headers:
          Object.entries(getValues('properties.headers') ?? {})
            .filter(([_, value]) => value)
            .reduce((acc: any, [key, value]) => {
              acc[key] = value;
              return acc;
            }, {}) || {}
      });
      setValue('isValidated', 'true', { shouldDirty: true }); //accepts string data type only;
      toast({
        variant: 'success',
        description: 'URL Validated Successfully'
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Validation Failed. Please check the URL and try again.'
      });
    } finally {
      setValidBtnLoader(false);
    }
  };

  const customHeaderFieldValidation = () =>
    currentHeader === webhookHeaders.CUSTOM_HEADER &&
    fields.some((field) => !field.headerKey || !field.headerValue);

  const handleCustomFields = () => {
    if (currentHeader === webhookHeaders.CUSTOM_HEADER) {
      const formattedFields = fields.reduce(
        (result, { headerKey, headerValue }) => ({
          ...result,
          [headerKey]: headerValue
        }),
        {}
      );
      setValue('properties.headers', formattedFields, { shouldDirty: true });
    }
  };

  useEffect(() => {
    // Skip until the fields have been initialised from the loaded webhook,
    // otherwise the initial empty fields overwrite the fetched custom headers.
    if (isInitialized.current && fields) {
      handleCustomFields();
    }
  }, [fields]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    !webhookDetails?.validated &&
      setValue('isValidated', '', { shouldDirty: true });
    !queryParams.get('id') && !getValues('secretKey') && regenerateToken();
    if (currentHeader === webhookHeaders.CUSTOM_HEADER) {
      const customHeaders = getValues('properties.headers') ?? {};
      // Keep only real custom headers, excluding the reserved keys used by the
      // other authentication types (basic/token/oauth) and empty placeholders.
      const customEntries = Object.entries(customHeaders).filter(
        ([key]) =>
          key !== '' && !defaultValues.properties.headers.hasOwnProperty(key)
      );
      setFields(
        customEntries.length
          ? customEntries.map(([headerKey, headerValue], idx) => ({
              headerKey,
              headerValue: headerValue as string,
              id: idx + 1
            }))
          : [{ ...customFieldDefaultValue }]
      );
    }
    isInitialized.current = true;
  }, []);

  return (
    <Flex justifyContent="center">
      {loader ? (
        <Flex alignItems="center" justifyContent="center">
          <Spinner size="md" />
        </Flex>
      ) : (
        <Box css={{ width: '100%' }}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            css={{ position: 'relative' }}
          >
            <Flex flexDirection="column" gap="$2">
              <Heading size="h5">Endpoint Validation</Heading>
              <Text size="md" css={{ color: '$neutral800' }}>
                Enter all the required fields to continue
              </Text>
            </Flex>
            <Button
              rightIcon={<ChevronRightIcon />}
              size="lg"
              onClick={() => {
                proceedHandler();
              }}
              disabled={
                !getValues('isValidated') ||
                customHeaderFieldValidation() ||
                Object.keys(errors?.properties?.headers || {}).length > 0 ||
                btnLoader
              }
            >
              Event Subscriptions
            </Button>
            <IconButton
              icon={<ChevronLeftIcon />}
              size="lg"
              variant="ghost"
              css={{ position: 'absolute', left: '-$30', top: 0 }}
              onClick={() => previousHandler()}
            />
          </Flex>
          <Flex alignItems="flex-start" gap="$40" css={{ marginTop: '$20' }}>
            <Box css={{ flex: 1 }}>
              <Box css={{ position: 'relative' }}>
                <FormInput
                  label="Secret Token"
                  css={{ paddingRight: '$20' }}
                  size="lg"
                  disabled
                  {...register('secretKey')}
                />
                <Flex
                  css={{
                    position: 'absolute',
                    top: 34,
                    right: 8,
                    cursor: 'pointer'
                  }}
                >
                  <CopyIcon
                    size={20}
                    color="#848484"
                    onClick={copyTokenToClipboard}
                  />
                </Flex>
                <Flex justifyContent="flex-end">
                  <Button
                    rightIcon={<ResetIcon />}
                    size="lg"
                    variant="ghost"
                    css={{ marginTop: '$8' }}
                    onClick={() => regenerateToken()}
                    loading={btnLoader}
                  >
                    Regenerate
                  </Button>
                </Flex>
              </Box>
              <Box
                css={{
                  marginTop: '$12'
                }}
              >
                <FormInput
                  label="Endpoint URL (POST Method)"
                  size="lg"
                  css={{ width: '100%' }}
                  error={errors?.url?.message}
                  errorBorder={errors?.url}
                  {...register('url', {
                    pattern: {
                      value: urlRegex,
                      message: 'Please enter a valid URL'
                    },
                    onChange: () =>
                      setValue('isValidated', '', { shouldDirty: true })
                  })}
                />
                <Flex justifyContent="flex-end">
                  <Button
                    size="lg"
                    variant="ghost"
                    rightIcon={<TickIcon />}
                    css={{ marginTop: '$8' }}
                    loading={validBtnLoader}
                    onClick={() => validateURL()}
                  >
                    Validate
                  </Button>
                </Flex>
              </Box>
            </Box>
            <AuthenticationHeader
              register={register}
              setValue={setValue}
              fields={fields}
              currentHeader={currentHeader}
              handleCurrentHeader={(value) => {
                setCurrentHeader(value);
                setValue(
                  'properties.headers',
                  value === 'customHeader'
                    ? customFieldDefaultValue
                    : defaultValues.properties.headers
                );
              }}
              setFields={setFields}
              trigger={trigger}
              errors={errors}
            />
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default AppAuthentication;
